import useAppStore from "@/context/store";
import { getSeoInlinks } from "@/services/qingzhou";
import useABTest from "@/utils/hooks/useABtest";
import useLaunchBottomVisibility from "@/utils/hooks/useLaunchBottomVisibility";
import { useNewLayout } from "@/utils/hooks/useNewLayout";
import { useThirdLoginCallback } from "@/utils/hooks/useThirdLogin";
import { useSafeState, useUpdateEffect } from "ahooks";
import classNames from "classnames";
import { useRouter } from "next/router";
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useState,
} from "react";
import { Transition } from "react-transition-group";
import HBButton from "../HBButton";
import HBIcon from "../HBIcon";
import HBLogo from "../HBLogo";
import { useLogin } from "../LoginModal";
import { LoginType } from "../LoginModal/login";
import { ThirdLogin } from "../LoginModal/shared/third";
import { IframeWxQrcode, WxLogin } from "../LoginModal/shared/wxLogin";
import { usePinActionAb } from "../PinCreator/PinDropdown/usePinActionAb";
import { TrackerA, TrackerDiv } from "../Tracker";
import styles from "./index.module.less";

export default function LaunchBottom({ inlinks, disabledModalOpenWidth }) {
  const { login } = useLogin();
  const { user, config } = useAppStore(({ user, userReady, config }) => ({
    user,
    userReady,
    config,
  }));
  const [qrcodeVis, setQrcodeVis] = useSafeState(false);
  const { launchBottomVisible, launchBottomDisabled, launchBottomIsHidePage } =
    useLaunchBottomVisibility();
  const { isNewLayout } = useNewLayout();

  const onLogin = useCallback(
    async (type) => {
      if (!user) {
        await login({ initType: type, target: "blank" });
      }
    },
    [login, user]
  );

  const { onWxLoginOk, onThirdLoginOk } = useThirdLoginCallback();

  const isUnifiedLogin = useABTest("login", "string_0", {
    old: "string_0",
    new: "string_1",
  });

  const pinActionAb = usePinActionAb();

  // 登录了就不用展示了
  if (launchBottomDisabled) {
    return null;
  }

  return (
    <>
      <div
        className={classNames([
          styles.launchBottom,
          !disabledModalOpenWidth ? "modalOpenWidth" : null,
          launchBottomVisible ? styles.active : null,
          launchBottomIsHidePage ? styles.isHome : null,
        ])}
        data-content-source="底部栏"
      >
        <div className={styles.wrap}>
          <div className={styles.left}>
            <div className={styles.img}>
              <HBLogo hasText width={66} height={20} />

              {/* <img
            src="//cdn.dancf.com/fe-assets/img/09b63a4cb94a263380ea9bb8074ec59a.png"
            alt="背景图"
          /> */}
            </div>
            <div className={styles.title}>发现、采集你喜欢的一切</div>
            <TrackerA
              className={styles.more}
              href={
                pinActionAb.new
                  ? "https://g.h5gdvip.com/p/1hb99rsn#1"
                  : "//s.gd-share.cn/4fqg"
              }
              target="_blank"
              rel="noreferrer nofollow"
              data-gd-click="button_click"
              data-button-name="了解更多"
            >
              了解更多
            </TrackerA>
          </div>
          {isNewLayout ? (
            <div className={styles.right}>
              <HBButton
                data-gd-click="button_click"
                data-button-name="立即登录"
                type="primary"
                onClick={() => onLogin(LoginType.PWD)}
              >
                立即登录
              </HBButton>
            </div>
          ) : (
            <div className={styles.right}>
              <TrackerDiv
                className={styles.phone}
                onClick={() => onLogin(LoginType.PWD)}
                data-gd-click="button_click"
                data-button-name="手机号登录"
              >
                <HBIcon type="icon" />
              </TrackerDiv>
              <ThirdLogin
                onOk={onThirdLoginOk}
                tooltipProps={{
                  placement: "top",
                  getPopupContainer: (trigger) => trigger,
                }}
              />
              <div
                onMouseEnter={() => {
                  setQrcodeVis(true);
                }}
                onMouseLeave={() => {
                  setQrcodeVis(false);
                }}
              >
                <TrackerDiv
                  className={styles.wechat}
                  onClick={() => onLogin(LoginType.WECHAT)}
                  data-gd-click="button_click"
                  data-button-name="微信扫码登录"
                >
                  <HBIcon type="ic_wechat1" />
                  微信扫码登录
                </TrackerDiv>

                {!isUnifiedLogin.new && (
                  <FadeRightIn bottom={60} appear={qrcodeVis}>
                    <div
                      className={styles.qrcodeWrap}
                      onClick={() => onLogin(LoginType.WECHAT)}
                    >
                      {qrcodeVis && (
                        <TrackerDiv
                          data-dialog-title="微信登录二维码"
                          data-gd-expose="dialog_expose"
                        >
                          {config.wxIframe ? (
                            <IframeWxQrcode size={70} onLogin={onWxLoginOk} />
                          ) : (
                            <WxLogin size={70} onLogin={onWxLoginOk} />
                          )}
                        </TrackerDiv>
                      )}
                    </div>
                  </FadeRightIn>
                )}
              </div>
            </div>
          )}
        </div>

        {!launchBottomIsHidePage && <PageInlinks inlinks={inlinks} />}
      </div>
    </>
  );
}

/**
 * 从右到左，透明到显示的渐变显示
 */
function FadeRightIn({
  children,
  duration = 300,
  delay = 100,
  bottom,
  appear,
} = {}) {
  const transitionStyles = {
    entering: { bottom, opacity: 0 },
    entered: { bottom: 55, opacity: 1 },
    exiting: { bottom: 55, opacity: 1 },
    exited: { bottom, opacity: 0 },
  };

  const defaultStyle = {
    transition: `all ${duration}ms ease-in-out`,
    opacity: 0,
    bottom,
    right: -5,
  };

  return (
    <Transition in={appear} timeout={delay}>
      {(state) =>
        Children.map(children, (child) => {
          if (!isValidElement(child)) {
            return null;
          }
          const childStyle = child.props.style || {};

          const childProps = {
            ...child.props,
            style: {
              ...childStyle,
              ...defaultStyle,
              ...transitionStyles[state],
            },
          };
          return cloneElement(child, childProps);
        })
      }
    </Transition>
  );
}

export function PageInlinks({ inlinks }) {
  const router = useRouter();
  const [links, setLinks] = useState(inlinks || []);
  const pathname = router.pathname;
  const isHome = pathname === "/";

  useUpdateEffect(() => {
    getSeoInlinks({
      current_url: isHome ? "home" : window.location.pathname,
      title: document.title,
    }).then((res) => {
      if (res?.length > 0) {
        setLinks(res);
      }
    });
  }, [router]);

  if (!links?.length) {
    return null;
  }

  return (
    <div className={styles.inlinks}>
      你可能感兴趣：
      {links?.map((item, index) => (
        <a key={index} href={item.url} target="_blank" rel="noreferrer">
          {item.keyword}
        </a>
      ))}
    </div>
  );
}
